/**
 * Keep the application's context boundary stable. Only the SDK bridge is
 * replaced when its authenticated principal or configuration changes.
 * Ghost display metadata never supplies notification credentials.
 */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NovuProvider as BaseNovuProvider, useNotifications, useNovu, useCounts } from '@novu/react';
import { useDispatch, useSelector } from 'react-redux';
import { async_loaduser } from '../../store/actions/studentsActions';
import api from '../../helpers/axiosconfig';
import NovuRealtimeToasts from './NovuRealtimeToasts';

const NovuContext = createContext(null);
const NOVU_FEED_TIMEOUT_MS = Number(process.env.REACT_APP_NOVU_FEED_TIMEOUT_MS || 12000);
const EMPTY_NOTIFICATIONS = [];
const noOp = () => {};
const COUNT_FILTERS = [{ read: false }, { seen: false }];
const SDK_CONFIG_FIELDS = ['subscriberId', 'applicationIdentifier', 'backendUrl', 'socketUrl', 'subscriberHash'];
const sameSdkConfig = (previous, next) => Boolean(previous?.novuAvailable !== false && next?.novuAvailable !== false && previous && next &&
  SDK_CONFIG_FIELDS.every(field => previous[field] === next[field]));

export const useNovuNotifications = () => {
  const context = useContext(NovuContext);
  if (!context) throw new Error('useNovuNotifications must be used within a NovuProvider');
  return context;
};

// This subtree owns the SDK, not the routed application. Publishing a snapshot
// updates the outer context without moving/remounting its application children.
const NovuNotificationManager = ({ bridgeKey, inboxScopeKey, inboxConfig, inboxConfigError, retryInboxConfig, isCurrentBridge, publishSnapshot }) => {
  const { notifications, error: feedError, isLoading, hasMore, fetchMore, refetch } = useNotifications();
  const { counts, error: countError, refetch: refetchUnreadCount } = useCounts({ filters: COUNT_FILTERS });
  const novu = useNovu();
  const unreadCount = counts?.find(entry => entry.filter?.read === false)?.count || 0;
  const unseenCount = counts?.find(entry => entry.filter?.seen === false)?.count || 0;
  const [sdkTimedOut, setSdkTimedOut] = useState(false);
  const activationRef = useRef(null);
  const [lease, setLease] = useState(null);
  const isActive = useCallback(() => Boolean(lease?.active && activationRef.current === lease &&
    isCurrentBridge(bridgeKey)), [lease, bridgeKey, isCurrentBridge]);

  useEffect(() => {
    const activation = { active: true };
    activationRef.current = activation;
    setLease(activation);
    return () => {
      activation.active = false;
      if (activationRef.current === activation) activationRef.current = null;
    };
  }, []);

  // Bind both notification-instance methods and client mutations. SDK objects
  // have prototype methods/private receivers; spreading them is not safe.
  const bindOperation = useCallback((operation, receiver) => {
    if (typeof operation !== 'function') return undefined;
    return async (...args) => {
      if (!isActive()) return undefined;
      try {
        const result = await operation.apply(receiver, args);
        if (!isActive()) return undefined;
        if (result?.error) throw result.error;
        return result;
      } catch (error) {
        if (isActive()) throw error;
        return undefined;
      }
    };
  }, [isActive]);
  const guardedNotifications = useMemo(() => Array.isArray(notifications) ? notifications.map(notification => {
    if (!notification || typeof notification !== 'object') return notification;
    const methods = new Map();
    return new Proxy(notification, { get(target, property) {
      const value = Reflect.get(target, property, target);
      if (typeof value !== 'function') return value;
      if (!methods.has(property)) methods.set(property, bindOperation(value, target));
      return methods.get(property);
    } });
  }) : notifications || EMPTY_NOTIFICATIONS, [notifications, bindOperation]);
  const guardedNovu = useMemo(() => !novu ? null : {
    notifications: Object.fromEntries(['read', 'readAll', 'delete', 'deleteAll'].map(method =>
      [method, bindOperation(novu.notifications?.[method], novu.notifications)])),
  }, [novu, bindOperation]);

  useEffect(() => {
    setSdkTimedOut(false);
    if (!isLoading) return undefined;
    const timeout = window.setTimeout(() => { if (isActive()) setSdkTimedOut(true); }, NOVU_FEED_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [isLoading, isActive]);

  const refetchAll = useCallback(async () => {
    if (!isActive()) return;
    try { await refetch?.(); }
    finally { if (isActive()) retryInboxConfig(); }
  }, [isActive, refetch, retryInboxConfig]);
  const fetchMoreForActive = useCallback((...args) => {
    if (isActive()) return fetchMore?.(...args);
  }, [isActive, fetchMore]);
  const refetchCountForActive = useCallback((...args) => {
    if (isActive()) return refetchUnreadCount?.(...args);
  }, [isActive, refetchUnreadCount]);
  const contextValue = useMemo(() => ({
    inboxScopeKey, canReadInbox: true,
    notifications: guardedNotifications, unseenCount, unreadCount,
    isLoading: Boolean(isLoading && !sdkTimedOut),
    isError: Boolean(feedError || countError || inboxConfigError || sdkTimedOut), hasMore,
    fetchMore: fetchMoreForActive, refetch: refetchAll,
    refetchUnreadCount: refetchCountForActive, novu: guardedNovu, inboxConfig,
  }), [inboxScopeKey, guardedNotifications, unseenCount, unreadCount, isLoading, sdkTimedOut, feedError, countError,
    inboxConfigError, hasMore, fetchMoreForActive, refetchAll, refetchCountForActive, guardedNovu, inboxConfig]);

  useEffect(() => {
    if (isActive()) publishSnapshot(bridgeKey, contextValue);
  }, [bridgeKey, contextValue, isActive, publishSnapshot]);

  return <NovuRealtimeToasts />;
};

const NovuProvider = ({ children }) => {
  const { ghostUserInfo = null, targetBinding = null, loading: ghostLoading = false } =
    useSelector(state => state.ghostLogin || {});
  const { user: baseUser, isAuthenticated, authLoading, canonicalSession, canonicalBootstrapRequired } =
    useSelector(state => state.students || {});
  const dispatch = useDispatch();
  const bootstrapRequestedFor = useRef(null);
  useEffect(() => {
    if (canonicalBootstrapRequired && !authLoading && !ghostLoading
      && bootstrapRequestedFor.current !== canonicalBootstrapRequired.requestId) {
      bootstrapRequestedFor.current = canonicalBootstrapRequired.requestId;
      void dispatch(async_loaduser(canonicalBootstrapRequired.revalidate === true
        ? { revalidationRequestId: canonicalBootstrapRequired.requestId }
        : { expectedSession: { ...canonicalBootstrapRequired } }));
    }
  }, [canonicalBootstrapRequired, authLoading, ghostLoading, dispatch]);
  const baseRole = baseUser?.UserType;
  const authenticatedUserId = baseUser?._id;
  const canonicalMatches = Boolean(canonicalSession
    && typeof canonicalSession.userId === 'string'
    && /^[a-f0-9]{24}$/i.test(canonicalSession.userId)
    && canonicalSession.userId === authenticatedUserId
    && canonicalSession.userType === baseRole
    && ['student', 'teacher', 'admin', 'accountant', 'counsellor'].includes(baseRole)
    && ['direct', 'ghost'].includes(canonicalSession.mode));
  const targetMatches = !targetBinding || Boolean(
    canonicalSession?.mode === 'ghost'
    && targetBinding.userId === canonicalSession.userId
    && targetBinding.userType === canonicalSession.userType
  );
  const canReadInbox = Boolean(isAuthenticated && !authLoading && !ghostLoading
    && canonicalMatches && targetMatches);
  const userId = canReadInbox ? canonicalSession.userId : null;
  // Browser hints may affect display preferences, never identity, role or mode.
  const user = canReadInbox && canonicalSession.mode === 'ghost'
    && ghostUserInfo?.UserType === baseRole
    ? { ...baseUser, language: ghostUserInfo.language || baseUser.language,
      Timezone: ghostUserInfo.Timezone || baseUser.Timezone }
    : baseUser;
  const principalKey = canReadInbox
    ? JSON.stringify([userId, canonicalSession.userType, canonicalSession.mode]) : null;
  const [scopeState, setScopeState] = useState({ principalKey, generation: 0 });
  // Render-time state adjustment resets only this scope, before children are
  // committed. Returning to A must not revive callbacks from an earlier A.
  let currentScope = scopeState;
  if (scopeState.principalKey !== principalKey) {
    currentScope = { principalKey, generation: scopeState.generation + 1 };
    setScopeState(currentScope);
  }
  const inboxScopeKey = principalKey ? JSON.stringify([principalKey, currentScope.generation]) : null;
  const userLocale = user?.language || null;
  const userTimezone = user?.Timezone || user?.userTimezone || null;
  const [configState, setConfigState] = useState(null);
  const [sdkSnapshot, setSdkSnapshot] = useState(null);
  const [configRefreshKey, setConfigRefreshKey] = useState(0);
  const requestGeneration = useRef(0);
  const currentScopeRef = useRef(null);
  const currentBridgeRef = useRef(null);

  const retryInboxConfig = useCallback(() => {
    if (inboxScopeKey && currentScopeRef.current === inboxScopeKey) setConfigRefreshKey(value => value + 1);
  }, [inboxScopeKey]);
  // Scope errors/degraded responses as well as credentials. Effects run after
  // render, so the previous account's result must already be ineligible here.
  const activeConfig = configState?.scopeKey === inboxScopeKey ? configState : null;
  const inboxConfig = activeConfig?.data || null;
  const isInboxConfigForActiveUser = Boolean(canReadInbox && inboxConfig?.novuAvailable !== false &&
    inboxConfig?.subscriberId && String(inboxConfig.subscriberId) === String(userId));
  const inboxConfigError = Boolean(activeConfig?.error || activeConfig?.timedOut);
  const bridgeKey = isInboxConfigForActiveUser ? JSON.stringify([inboxScopeKey, activeConfig.sdkGeneration]) : null;
  // Speculative renders can be abandoned. They must never revoke the
  // currently committed account's callbacks by mutating shared refs.
  useLayoutEffect(() => {
    currentScopeRef.current = inboxScopeKey;
    currentBridgeRef.current = bridgeKey;
    return () => {
      currentScopeRef.current = null;
      currentBridgeRef.current = null;
    };
  }, [inboxScopeKey, bridgeKey]);

  const isCurrentBridge = useCallback(key => Boolean(key && currentBridgeRef.current === key), []);
  const publishSnapshot = useCallback((key, value) => {
    if (currentBridgeRef.current === key) setSdkSnapshot(previous =>
      previous?.key === key && previous.value === value ? previous : { key, value });
  }, []);

  useEffect(() => {
    if (!canReadInbox) return undefined;
    let active = true;
    const generation = ++requestGeneration.current;
    setConfigState(previous => !active ? previous : ({
      scopeKey: inboxScopeKey,
      requestGeneration: generation,
      data: previous?.scopeKey === inboxScopeKey ? previous.data : null,
      sdkGeneration: previous?.scopeKey === inboxScopeKey ? previous.sdkGeneration : null,
      pending: true, error: false, timedOut: false,
    }));
    const timeout = window.setTimeout(() => {
      if (active) setConfigState(previous => !active || previous?.requestGeneration !== generation
        ? previous : ({ ...previous, pending: false, error: true, timedOut: true }));
    }, NOVU_FEED_TIMEOUT_MS);
    const fetchConfig = async () => {
      try {
        const response = await api.get('/notifications/inbox-config', {
          validateStatus: status => (status >= 200 && status < 300) || status === 304,
        });
        if (!active) return;
        if (response.status === 304) {
          setConfigState(previous => !active || previous?.requestGeneration !== generation
            ? previous : ({ ...previous, pending: false, timedOut: false,
            error: !previous?.data?.subscriberId || previous.data.novuAvailable === false || String(previous.data.subscriberId) !== String(userId),
          }));
          return;
        }
        const data = response.data;
        setConfigState(previous => !active || previous?.requestGeneration !== generation
          ? previous : ({ scopeKey: inboxScopeKey, requestGeneration: generation, data,
          sdkGeneration: previous?.scopeKey === inboxScopeKey && sameSdkConfig(previous.data, data)
            ? previous.sdkGeneration : generation,
          pending: false, timedOut: false,
          error: !data?.subscriberId || data.novuAvailable === false || String(data.subscriberId) !== String(userId),
        }));
      } catch (error) {
        if (!active) return;
        setConfigState(previous => !active || previous?.requestGeneration !== generation
          ? previous : ({ ...previous, pending: false, error: true, timedOut: false }));
        if (error?.response?.status !== 401 && error?.response?.status !== 500) {
          console.warn('[NovuProvider] Failed to load Novu inbox config', {
            status: error?.response?.status, baseURL: error?.config?.baseURL, url: error?.config?.url,
          });
        }
      } finally { window.clearTimeout(timeout); }
    };
    fetchConfig();
    return () => { active = false; window.clearTimeout(timeout); };
  }, [canReadInbox, inboxScopeKey, userId, userLocale, userTimezone, configRefreshKey]);

  // Memoizing the SDK-only element prevents a snapshot publication from
  // re-rendering the hooks that produced that same snapshot.
  const sdkBridge = useMemo(() => bridgeKey ? (
    <BaseNovuProvider key={bridgeKey}
      subscriberId={inboxConfig.subscriberId} applicationIdentifier={inboxConfig.applicationIdentifier}
      backendUrl={inboxConfig.backendUrl} socketUrl={inboxConfig.socketUrl} subscriberHash={inboxConfig.subscriberHash}
      initialFetchingStrategy={{ fetchNotifications: true, fetchUserPreferences: true }}>
      <NovuNotificationManager bridgeKey={bridgeKey} inboxScopeKey={inboxScopeKey} inboxConfig={inboxConfig}
        inboxConfigError={inboxConfigError} retryInboxConfig={retryInboxConfig}
        isCurrentBridge={isCurrentBridge} publishSnapshot={publishSnapshot} />
    </BaseNovuProvider>
  ) : null, [bridgeKey, inboxScopeKey, inboxConfig, inboxConfigError, retryInboxConfig, isCurrentBridge, publishSnapshot]);

  const fallbackContext = {
    inboxScopeKey, canReadInbox, notifications: EMPTY_NOTIFICATIONS, unseenCount: 0, unreadCount: 0,
    isLoading: Boolean(canReadInbox && !isInboxConfigForActiveUser && !inboxConfigError && (!activeConfig || activeConfig.pending)),
    isError: inboxConfigError, hasMore: false, fetchMore: noOp, refetch: retryInboxConfig,
    refetchUnreadCount: noOp, novu: null, inboxConfig: null,
  };
  const contextValue = bridgeKey && sdkSnapshot?.key === bridgeKey
    ? { ...sdkSnapshot.value, isError: Boolean(sdkSnapshot.value.isError || inboxConfigError) }
    : fallbackContext;
  return (
    <NovuContext.Provider value={contextValue}>
      {children}
      {sdkBridge}
    </NovuContext.Provider>
  );
};

export default NovuProvider;
